import React, { useEffect, useState, useCallback } from "react";
import { CreditCard, Plus, ArrowUpCircle, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";

const STATUS_LABEL = { trial: "Período de teste", active: "Ativa", past_due: "Pagamento pendente", cancelled: "Cancelada", expired: "Expirada" };
const brl = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function TeamPanel({ isActive }) {
    const [team, setTeam] = useState(null);
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("editor");
    const [busy, setBusy] = useState(false);
    const loadTeam = () => api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    useEffect(() => { loadTeam(); }, []);
    if (!team) return null;

    const invite = async () => {
        if (!email.trim()) return;
        setBusy(true);
        try { await api.post("/team/invite", { email, role, origin_url: window.location.origin }); toast.success(`Convite enviado para ${email}.`); setEmail(""); loadTeam(); }
        catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "object" ? d.message : (d || "Erro ao convidar.")); }
        finally { setBusy(false); }
    };
    const removeMember = async (id) => { await api.delete(`/team/member/${id}`); toast.success("Usuário removido."); loadTeam(); };
    const cancelInvite = async (t) => { await api.delete(`/team/invite/${t}`); toast.success("Convite cancelado."); loadTeam(); };
    const changeRole = async (id, r) => {
        try { await api.put(`/team/member/${id}/role`, { role: r }); toast.success(r === "viewer" ? "Membro agora tem acesso somente leitura." : "Membro agora pode editar."); loadTeam(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erro ao alterar permissão."); }
    };
    const roleBadge = (r) => r === "viewer"
        ? <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">Somente leitura</span>
        : <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Editor</span>;

    return (
        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm" data-testid="team-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-petroleum" /> Equipe (usuários da conta)</h3>
                <span className="text-sm text-slate-500">{team.seats_used} de {team.user_limit} usuário(s)</span>
            </div>
            {!team.is_owner ? (
                <p className="mt-3 text-sm text-slate-500">Você é membro da conta de <strong>{team.owner?.name || team.owner?.email}</strong>. Apenas o titular gerencia a equipe.</p>
            ) : (
                <>
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email" placeholder="email@daequipe.com"
                            className="flex-1 rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-3 py-2 text-sm transition-colors" />
                        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-invite-role"
                            className="rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-3 py-2 text-sm transition-colors bg-white">
                            <option value="editor">Editor (acesso total)</option>
                            <option value="viewer">Somente leitura</option>
                        </select>
                        <button onClick={invite} disabled={busy || !isActive || (team.seats_used + team.pending_invites) >= team.user_limit} data-testid="team-invite-button"
                            className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50">Convidar</button>
                    </div>
                    {(team.seats_used + team.pending_invites) >= team.user_limit && <p className="mt-2 text-xs text-slate-400">Limite de usuários do plano atingido. Faça upgrade para adicionar mais.</p>}
                    <ul className="mt-4 divide-y divide-slate-100">
                        <li className="py-2 flex items-center justify-between text-sm"><span className="text-slate-800">{team.owner?.name} <span className="text-slate-400">· {team.owner?.email}</span></span><span className="text-xs px-2 py-0.5 rounded-full bg-petroleum/10 text-petroleum">Titular</span></li>
                        {team.members.map((m) => (
                            <li key={m.user_id} className="py-2 flex items-center justify-between gap-2 text-sm" data-testid={`team-member-${m.user_id}`}>
                                <span className="text-slate-800 flex-1 truncate">{m.name} <span className="text-slate-400">· {m.email}</span></span>
                                <select value={m.member_role || "editor"} onChange={(e) => changeRole(m.user_id, e.target.value)} data-testid={`team-member-role-${m.user_id}`}
                                    className="rounded-md border border-slate-200 text-xs px-2 py-1 bg-white outline-none focus:border-petroleum">
                                    <option value="editor">Editor</option>
                                    <option value="viewer">Somente leitura</option>
                                </select>
                                <button onClick={() => removeMember(m.user_id)} className="text-xs text-red-500 hover:underline">Remover</button>
                            </li>
                        ))}
                        {team.invites.map((i) => (
                            <li key={i.email} className="py-2 flex items-center justify-between gap-2 text-sm text-slate-500" data-testid={`team-invite-${i.email}`}>
                                <span className="flex-1 truncate">{i.email} <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 ml-1">convite pendente</span></span>
                                {roleBadge(i.role)}
                                <button onClick={() => cancelInvite(i.token)} className="text-xs text-slate-400 hover:underline">Cancelar</button>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

function UsageBar({ label, used, limit }) {
    const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    const danger = pct >= 100, warn = pct >= 80;
    return (
        <div data-testid={`usage-${label}`}>
            <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{label}</span><span className="font-mono font-semibold text-slate-800">{used} de {limit}</span></div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${danger ? "bg-red-500" : warn ? "bg-mineral" : "bg-petroleum"}`} style={{ width: `${pct}%` }} /></div>
        </div>
    );
}

export default function SubscriptionPage() {
    const { user, refresh } = useAuth();
    const [sub, setSub] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => api.get("/subscription").then((r) => setSub(r.data)).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);

    const isActive = user?.subscription_status === "active";

    const subscribe = async (plan) => {
        setBusy(true);
        try { const res = await api.post("/payments/checkout", { origin_url: window.location.origin, plan }); window.location.href = res.data.checkout_url; }
        catch (e) { setBusy(false); toast.error("Erro ao iniciar assinatura."); }
    };

    const changePlan = async (plan) => {
        setBusy(true);
        try {
            const res = await api.post("/payments/change-plan", { plan });
            toast.success(res.data.applied === "imediato" ? `Plano alterado para ${res.data.plan}.` : `Mudança para ${res.data.plan} agendada para o fim do ciclo.`);
            await refresh(); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao alterar plano."); }
        finally { setBusy(false); }
    };

    const buyAdditional = async (slots) => {
        setBusy(true);
        try { const res = await api.post("/payments/additional-process", { slots }); toast.success(`Processos adicionais: ${res.data.additional_process_slots}. Limite: ${res.data.effective_limit}.`); await refresh(); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erro ao contratar."); }
        finally { setBusy(false); }
    };

    if (!sub) return <div className="max-w-4xl"><PageHeader title="Meu Plano" subtitle="Carregando..." /></div>;

    return (
        <div className="max-w-4xl space-y-6" data-testid="subscription-page">
            <PageHeader title="Meu Plano" subtitle="Gerencie seu plano, uso e processos adicionais." />
            <TeamPanel isActive={isActive} />

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-md bg-petroleum/10 flex items-center justify-center"><CreditCard className="w-6 h-6 text-petroleum" /></div>
                        <div>
                            <div className="font-heading font-bold text-slate-900" data-testid="current-plan-name">{sub.plan}</div>
                            <div className="text-sm text-slate-500">{brl(sub.price)} / mês</div>
                        </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${isActive ? "bg-green-100 text-green-700 border-green-200" : "bg-mineral/15 text-mineralHover border-mineral/30"}`} data-testid="sub-status">
                        {STATUS_LABEL[sub.status] || sub.status}
                    </span>
                </div>

                {sub.status === "trial" && sub.trial_ends_at && (
                    <p className="mt-4 text-sm text-slate-500">Seu período de teste termina em {new Date(sub.trial_ends_at).toLocaleDateString("pt-BR")}.</p>
                )}
                {sub.pending_plan_name && (
                    <p className="mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">Mudança agendada para <strong>{sub.pending_plan_name}</strong> no início do próximo ciclo.</p>
                )}
                {sub.over_limit && (
                    <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="over-limit-warning">
                        Você possui {sub.processes_used} processos cadastrados, mas seu plano atual permite até {sub.effective_process_limit}. Seus dados foram preservados. Faça upgrade ou ajuste sua quantidade de processos para liberar novos cadastros.
                    </p>
                )}

                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <UsageBar label="Processos ANM" used={sub.processes_used} limit={sub.effective_process_limit} />
                    <UsageBar label="Consultas IA" used={sub.consultations_used} limit={sub.consultation_limit} />
                    <UsageBar label="Tokens" used={sub.tokens_used} limit={sub.token_limit} />
                    <UsageBar label="Usuários" used={sub.users_used} limit={sub.user_limit} />
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
                <h3 className="font-heading font-bold text-slate-900 mb-3">Resumo da assinatura</h3>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Processos incluídos no plano</span><span className="font-semibold">{sub.process_limit}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Processos adicionais contratados</span><span className="font-semibold">{sub.additional_process_slots}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Limite total atual</span><span className="font-semibold">{sub.effective_process_limit}</span></div>
                    <div className="border-t border-slate-100 my-2" />
                    <div className="flex justify-between"><span className="text-slate-600">Valor do plano</span><span className="font-semibold">{brl(sub.price)}/mês</span></div>
                    {sub.additional_process_slots > 0 && (
                        <div className="flex justify-between"><span className="text-slate-600">Processos adicionais ({sub.additional_process_slots} × {brl(sub.additional_process_unit_price)})</span><span className="font-semibold">{brl(sub.additional_process_total)}/mês</span></div>
                    )}
                    <div className="flex justify-between text-base pt-2 border-t border-slate-200"><span className="font-bold text-slate-900">TOTAL DA ASSINATURA</span><span className="font-black text-petroleum" data-testid="total-price">{brl(sub.total_price)}/mês</span></div>
                </div>

                {isActive && (
                    <div className="mt-5 flex flex-wrap gap-3 items-center">
                        {sub.additional_process_slots < sub.max_additional_slots ? (
                            <button onClick={() => buyAdditional(sub.additional_process_slots + 1)} disabled={busy} data-testid="buy-additional-button" className="flex items-center gap-2 bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-5 py-2.5 font-semibold transition-colors disabled:opacity-60">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Comprar processo adicional (+{brl(sub.additional_process_unit_price)}/mês)
                            </button>
                        ) : (
                            <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
                                Você já possui o número máximo de processos adicionais permitido neste plano.{sub.next_plan_name ? ` O plano ${sub.next_plan_name} permite gerenciar até ${sub.next_plan_processes} processos ANM.` : ""}
                            </div>
                        )}
                        {sub.additional_process_slots > 0 && (
                            <button onClick={() => buyAdditional(sub.additional_process_slots - 1)} disabled={busy} data-testid="remove-additional-button" className="text-sm text-slate-500 hover:text-red-600 font-semibold px-3">Remover 1 adicional</button>
                        )}
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                {sub.all_plans.map((p) => {
                    const current = p.key === sub.plan_key && isActive;
                    return (
                        <div key={p.key} className={`bg-white border rounded-md p-5 shadow-sm ${current ? "border-petroleum ring-1 ring-petroleum" : "border-slate-200"}`} data-testid={`plan-card-${p.key}`}>
                            <div className="font-heading font-black text-slate-900">{p.name}</div>
                            <div className="text-2xl font-black text-petroleum mt-1">{brl(p.price)}<span className="text-sm font-normal text-slate-400">/mês</span></div>
                            <ul className="mt-3 text-sm text-slate-600 space-y-1">
                                <li>{p.process_limit} processos ANM</li>
                                <li>{p.consultation_limit} consultas IA/mês</li>
                                <li>{(p.token_limit / 1000).toLocaleString("pt-BR")} mil tokens/mês</li>
                                <li>{p.user_limit} usuário(s)</li>
                            </ul>
                            {current ? (
                                <div className="mt-4 text-center text-sm font-semibold text-petroleum bg-petroleum/5 rounded-sm py-2">Plano atual</div>
                            ) : !isActive ? (
                                <button onClick={() => subscribe(p.key)} disabled={busy} data-testid={`subscribe-${p.key}`} className="mt-4 w-full bg-mineral hover:bg-mineralHover text-white rounded-sm py-2.5 font-bold transition-colors disabled:opacity-60">Assinar</button>
                            ) : (
                                <button onClick={() => changePlan(p.key)} disabled={busy} data-testid={`change-${p.key}`} className="mt-4 w-full flex items-center justify-center gap-1.5 border-2 border-petroleum text-petroleum hover:bg-petroleum/5 rounded-sm py-2.5 font-bold transition-colors disabled:opacity-60">
                                    <ArrowUpCircle className="w-4 h-4" /> {p.price > sub.price ? "Fazer upgrade" : "Mudar para este"}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            <p className="text-xs text-slate-400">Upgrade é aplicado imediatamente (com proração). Downgrade entra em vigor no início do próximo ciclo. Seus processos e dados nunca são apagados. Cobranças processadas com segurança pelo gateway de pagamento.</p>
        </div>
    );
}
